import React, { useState, useEffect } from 'react';
import { useAPR } from '../context/APRContext';
import { useAuth } from '../context/AuthContext';
import { Application } from '../types';
import apiService, { authFetch } from '../services/api';
import QAViewModal from '../components/QAViewModal';
import QAAssessmentModal, { AssessmentResults } from '../components/QAAssessmentModal';

// Type for grouped model data
interface ModelGroup {
  modelId: string;
  modelName: string;
  modelType: 'Model' | 'Run';
  frameworkName?: string;
  applications: Application[];
}

const ApplicationPortfolioQA: React.FC = () => {
  const { state, dispatch } = useAPR();
  const { currentUser } = useAuth();
  const [selectedApplicationId, setSelectedApplicationId] = useState<string>('All');
  const [selectedOwner, setSelectedOwner] = useState<string>('All');
  const [inProcessApplications, setInProcessApplications] = useState<Application[]>([]);
  const [applicationModels, setApplicationModels] = useState<Record<string, { name: string; type: 'Model' | 'Run' }>>({});
  const [modelGroups, setModelGroups] = useState<ModelGroup[]>([]);
  const [expandedModels, setExpandedModels] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'tree' | 'flat'>('tree');
  const [loading, setLoading] = useState(true);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showQAModal, setShowQAModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<{ id: string; name: string; aprRunId?: string } | null>(null);
  const [qaStatuses, setQAStatuses] = useState<Record<string, any>>({});
  const [excludedParentCount, setExcludedParentCount] = useState(0);
  const [finalDispositions, setFinalDispositions] = useState<Record<string, string | null>>({});

  // Load final dispositions to determine which applications are locked
  const loadFinalDispositions = async () => {
    try {
      const response = await authFetch('/api/final-disposition');
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          const dispositionMap: Record<string, string | null> = {};
          data.data.forEach((item: any) => {
            dispositionMap[item.application_id] = item.final_4r_model || null;
          });
          setFinalDispositions(dispositionMap);
          console.log('🔒 Final dispositions loaded:', dispositionMap);
        }
      }
    } catch (error) {
      console.error('Error loading final dispositions:', error);
    }
  };

  // Load data when component mounts and when APR context changes
  useEffect(() => {
    console.log('🚀 ApplicationPortfolioQA: Component mounted or context changed, loading data...');
    loadInProcessApplications();
    loadFinalDispositions();
  }, [state.models, state.aprRuns, state.applications]);

  // Load Q&A status for applications
  useEffect(() => {
    if (inProcessApplications.length > 0) {
      console.log('📋 Applications loaded, loading Q&A statuses...');
      loadQAStatuses();
    }
  }, [inProcessApplications]);

  const loadInProcessApplications = async () => {
    setLoading(true);
    try {
      console.log('🔍 Loading In-Process applications...');
      console.log('📊 APR Context State:', {
        applications: state.applications.length,
        aprRuns: state.aprRuns.length,
        models: state.models.length
      });

      // Get applications that are in "In-Process" APR runs or models
      const inProcessAPRRuns = state.aprRuns.filter(run => run.status === 'In-Process');
      const inProcessAPRModels = state.models.filter(model => model.status === 'In-Process');

      console.log('🏃 In-Process APR Runs:', inProcessAPRRuns.map(r => ({ id: r.id, name: r.rationalizationName })));
      console.log('🎯 In-Process APR Models:', inProcessAPRModels.map(m => ({ id: m.id, name: m.name })));

      const allInProcessApps = new Map<string, Application>();
      const appModelMapping: Record<string, { name: string; type: 'Model' | 'Run' }> = {};
      const groups: ModelGroup[] = [];

      // Get applications from In-Process APR runs
      for (const run of inProcessAPRRuns) {
        if (run.selectedApplicationIds) {
          console.log(`📋 APR Run "${run.rationalizationName}" has ${run.selectedApplicationIds.length} selected apps`);
          const runApps: Application[] = [];
          for (const appId of run.selectedApplicationIds) {
            const app = state.applications.find(a => a.id === appId);
            if (app && !app.isParent) {
              allInProcessApps.set(app.id, app);
              appModelMapping[app.id] = { name: run.rationalizationName, type: 'Run' };
              runApps.push(app);
              console.log(`✅ Added app: ${app.name} (from run: ${run.rationalizationName})`);
            } else if (app && app.isParent) {
              console.log(`⏭️ Skipping parent app: ${app.name}`);
            } else {
              console.log(`❌ App not found in context: ${appId}`);
            }
          }
          if (runApps.length > 0) {
            groups.push({
              modelId: run.id,
              modelName: run.rationalizationName,
              modelType: 'Run',
              applications: runApps
            });
          }
        }
      }

      // Get applications from In-Process APR models using API
      for (const model of inProcessAPRModels) {
        try {
          console.log(`🔗 Loading applications for model: ${model.name}`);
          const modelApplications = await apiService.getModelApplications(model.id);
          console.log(`📦 Model "${model.name}" has ${modelApplications.length} applications`);
          const modelApps: Application[] = [];
          modelApplications.forEach((app: Application) => {
            if (!app.isParent) {
              allInProcessApps.set(app.id, app);
              appModelMapping[app.id] = { name: model.name, type: 'Model' };
              modelApps.push(app);
              console.log(`✅ Added app from model: ${app.name} (from model: ${model.name})`);
            }
          });
          if (modelApps.length > 0) {
            groups.push({
              modelId: model.id,
              modelName: model.name,
              modelType: 'Model',
              frameworkName: (model as any).frameworkName || undefined,
              applications: modelApps
            });
          }
        } catch (error) {
          console.error(`❌ Error loading applications for model ${model.id}:`, error);
        }
      }

      const allApps = Array.from(allInProcessApps.values());
      const excludedParentApps = state.applications.filter(app =>
        app.isParent && (
          inProcessAPRRuns.some(r => r.selectedApplicationIds?.includes(app.id)) ||
          groups.some(g => g.applications.some(a => a.id === app.id))
        )
      );

      console.log(`🎉 Total In-Process applications found: ${allApps.length}`);
      console.log(`🚫 Excluded parent applications: ${excludedParentApps.length}`);
      console.log(`✅ Eligible child applications: ${allApps.length}`);
      console.log('📱 Applications:', allApps.map(app => ({ id: app.id, name: app.name })));
      console.log('🏗️ App-Model mapping:', appModelMapping);
      console.log('🌳 Model Groups:', groups);

      setInProcessApplications(allApps);
      setExcludedParentCount(excludedParentApps.length);
      setApplicationModels(appModelMapping);
      setModelGroups(groups);
      // Collapse all models by default
      setExpandedModels(new Set());
    } catch (error) {
      console.error('❌ Error loading in-process applications:', error);
      setInProcessApplications([]);
      setModelGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const loadQAStatuses = async () => {
    try {
      console.log(`🔄 Loading QA statuses for ${inProcessApplications.length} applications (lightweight)...`);
      // Single lightweight query — just counts, no scoring data
      const statuses = await apiService.getQAStatuses();
      const statusMap: Record<string, any> = {};

      for (const row of statuses) {
        const total = parseInt(row.total_questions as any) || 0;
        const answered = parseInt(row.answered_questions as any) || 0;
        statusMap[row.application_id] = {
          totalQuestions: total,
          answeredQuestions: answered,
          completionPercentage: total > 0 ? Math.round((answered / total) * 100) : 0
        };
      }

      console.log('📊 Final QA Status Map:', Object.keys(statusMap).length, 'apps');
      setQAStatuses(statusMap);
    } catch (error) {
      console.error('❌ Error loading QA statuses:', error);
    }
  };

  // Function to determine Q&A status for an application using real assessment data
  const getQAStatus = (app: Application) => {
    const appQAStatus = qaStatuses[app.id];
    
    if (!appQAStatus) {
      return { status: 'Not Started', className: 'status-not-started', icon: '⭕' };
    }
    
    const { totalQuestions, answeredQuestions } = appQAStatus;
    
    if (answeredQuestions === 0) {
      return { status: 'Not Started', className: 'status-not-started', icon: '⭕' };
    } else if (answeredQuestions < totalQuestions) {
      return { status: 'In-Process', className: 'status-in-process', icon: '⏳' };
    } else {
      return { status: 'Complete', className: 'status-complete', icon: '✅' };
    }
  };

  // Toggle model expansion in tree view
  const toggleModelExpansion = (modelId: string) => {
    setExpandedModels(prev => {
      const newSet = new Set(prev);
      if (newSet.has(modelId)) {
        newSet.delete(modelId);
      } else {
        newSet.add(modelId);
      }
      return newSet;
    });
  };

  // Get aggregate stats for a model group
  const getModelStats = (group: ModelGroup) => {
    let totalQuestions = 0;
    let answeredQuestions = 0;
    let completedApps = 0;
    let inProgressApps = 0;
    let notStartedApps = 0;

    group.applications.forEach(app => {
      const status = qaStatuses[app.id];
      if (status) {
        totalQuestions += status.totalQuestions || 0;
        answeredQuestions += status.answeredQuestions || 0;
        if (status.answeredQuestions === 0) {
          notStartedApps++;
        } else if (status.answeredQuestions < status.totalQuestions) {
          inProgressApps++;
        } else {
          completedApps++;
        }
      } else {
        notStartedApps++;
      }
    });

    return {
      totalQuestions,
      answeredQuestions,
      completedApps,
      inProgressApps,
      notStartedApps,
      totalApps: group.applications.length,
      completionPercentage: totalQuestions > 0 ? Math.round((answeredQuestions / totalQuestions) * 100) : 0
    };
  };

  // Filter applications within a model group
  const filterGroupApplications = (apps: Application[]) => {
    return apps.filter(app => {
      // Role-based access control
      if (currentUser?.role === 'Application Owner') {
        if (app.appOwnerEmail !== currentUser.email) {
          return false;
        }
      }
      // Application ID filter
      if (selectedApplicationId !== 'All') {
        const filterText = selectedApplicationId.toLowerCase();
        const applicationIdMatch = (app.applicationIdCode && app.applicationIdCode.toLowerCase().includes(filterText)) ||
                                   app.name.toLowerCase().includes(filterText);
        if (!applicationIdMatch) {
          return false;
        }
      }
      // Application Owner filter
      if ((currentUser?.role === 'Administrator' || currentUser?.role === 'APR Analyst') && selectedOwner !== 'All') {
        if (app.applicationOwner !== selectedOwner) {
          return false;
        }
      }
      return true;
    });
  };

  // Get applications that the current user can see (for statistics)
  const visibleApplications = inProcessApplications.filter(app => {
    // Role-based access control
    if (currentUser?.role === 'Application Owner') {
      return app.appOwnerEmail === currentUser?.email;
    }
    // Administrators and Users can see all applications
    return true;
  });

  // Apply role-based filtering and other filters
  const filteredApplications = inProcessApplications.filter(app => {
    // Role-based access control
    if (currentUser?.role === 'Application Owner') {
      // Application Owners can only see their own applications
      if (app.appOwnerEmail !== currentUser.email) {
        return false;
      }
    }
    // Administrators and Users can see and conduct Q&A on all In-Process applications regardless of ownership
    
    // Application ID filter - support partial text matching
    if (selectedApplicationId !== 'All') {
      const filterText = selectedApplicationId.toLowerCase();
      const applicationIdMatch = (app.applicationIdCode && app.applicationIdCode.toLowerCase().includes(filterText)) ||
                                 app.name.toLowerCase().includes(filterText);
      if (!applicationIdMatch) {
        return false;
      }
    }
    
    // Application Owner filter (only for Administrator and APR Analyst roles)
    if ((currentUser?.role === 'Administrator' || currentUser?.role === 'APR Analyst') && selectedOwner !== 'All') {
      if (app.applicationOwner !== selectedOwner) {
        return false;
      }
    }
    
    return true;
  });

  const handleViewQA = (application: Application, event?: React.MouseEvent) => {
    // Prevent event bubbling and double clicks
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    console.log('ApplicationPortfolioQA: handleViewQA called for', application.name);
    
    // Prevent opening if modal is already open
    if (showViewModal) {
      console.log('ApplicationPortfolioQA: Modal already open, ignoring');
      return;
    }
    
    // Find the APR run ID for this application
    let aprRunId: string | undefined;
    
    // First check in APR runs
    const aprRun = state.aprRuns.find(run => 
      run.status === 'In-Process' && 
      run.selectedApplicationIds?.includes(application.id)
    );
    
    if (aprRun) {
      aprRunId = aprRun.id;
    }
    
    console.log('ApplicationPortfolioQA: Setting selected application:', {
      id: application.id,
      name: application.name,
      aprRunId
    });
    
    // Set selected application and show modal immediately
    setSelectedApplication({
      id: application.id,
      name: application.name,
      aprRunId
    });
    
    console.log('ApplicationPortfolioQA: Setting showViewModal to true');
    setShowViewModal(true);
  };

  const handleStartQA = (application: Application, event?: React.MouseEvent) => {
    // Prevent event bubbling and double clicks
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    // Prevent opening if already opening or modal is already open
    if (showQAModal) return;
    
    // Find the APR run ID for this application
    let aprRunId: string | undefined;
    
    // First check in APR runs
    const aprRun = state.aprRuns.find(run => 
      run.status === 'In-Process' && 
      run.selectedApplicationIds?.includes(application.id)
    );
    
    if (aprRun) {
      aprRunId = aprRun.id;
    }
    
    // Set selected application and show modal immediately
    setSelectedApplication({
      id: application.id,
      name: application.name,
      aprRunId
    });
    
    setShowQAModal(true);
  };

  // Function to check if APR Model can be reverted (all apps have "Not Started" Q&A status)
  const canRevertModel = (modelId: string) => {
    const modelApplications = inProcessApplications.filter(app => {
      // Check if app belongs to this model
      return state.models.find(m => m.id === modelId && m.status === 'In-Process');
    });
    
    // All applications must have "Not Started" status
    return modelApplications.every(app => getQAStatus(app).status === 'Not Started');
  };

  // Function to revert APR Model back to Scheduled status
  const handleRevertModel = async (modelId: string, modelName: string) => {
    if (!window.confirm(`Are you sure you want to revert "${modelName}" back to Scheduled status? This will remove all applications from the Q&A process.`)) {
      return;
    }

    try {
      // Find the current model to get all its properties
      const currentModel = state.models.find(m => m.id === modelId);
      if (!currentModel) {
        alert('APR Model not found.');
        return;
      }

      // Create updated model with all required properties
      const updatedModel = {
        ...currentModel,
        status: 'Scheduled' as const
      };

      await apiService.updateModel(modelId, {
        name: currentModel.name,
        status: 'Scheduled'
      });
      
      // Update local state
      dispatch({
        type: 'UPDATE_MODEL',
        payload: updatedModel
      });
      
      // Refresh applications list
      loadInProcessApplications();
      
      alert(`APR Model "${modelName}" has been successfully reverted to Scheduled status.`);
    } catch (error) {
      console.error('Error reverting model:', error);
      alert('Failed to revert APR Model status. Please try again.');
    }
  };

  // Modal event handlers
  const handleCloseViewModal = async () => {
    console.log('🔄 Modal closed, refreshing QA statuses...');
    setShowViewModal(false);
    setSelectedApplication(null);
    // Refresh the QA statuses after closing modal
    if (inProcessApplications.length > 0) {
      await loadQAStatuses();
    }
  };

  const handleCloseQAModal = async () => {
    setShowQAModal(false);
    setSelectedApplication(null);
    // Refresh the QA statuses after closing modal
    if (inProcessApplications.length > 0) {
      await loadQAStatuses();
    }
  };

  const handleCompleteAssessment = async (results: AssessmentResults) => {
    console.log('Assessment completed:', results);
    await handleCloseQAModal();
  };

  return (
    <div className="page page-fixed-header">
      {/* Fixed Header Section - Does NOT scroll */}
      <div className="fixed-header-section">
        {/* Header */}
        <div className="page-header" style={{ marginBottom: '8px' }}>
          <div className="page-header__content">
            <div className="page-header__title-section">
              <h1 className="page-header__title">Application Portfolio Q&A</h1>
              <p className="page-header__subtitle">
                Answer questionnaire for applications in "In-Process" APR models and runs
              </p>
            </div>
          </div>
        </div>

        {/* Notice about excluded parent applications */}
        {!loading && excludedParentCount > 0 && (
          <div style={{
            marginBottom: '8px',
            padding: '8px 12px',
            backgroundColor: '#fffbeb',
            border: '1px solid #f59e0b',
            borderRadius: '6px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#92400e', fontSize: '13px' }}>
              <span>ℹ️</span>
              <span>
                <strong>{excludedParentCount} parent application{excludedParentCount > 1 ? 's' : ''}</strong> excluded.
                Only child applications are eligible for Q&A.
              </span>
            </div>
          </div>
        )}

        {/* Filter Section - Closer to header */}
        {!loading && (
          <div className="md-filter-container" style={{
            marginBottom: '0',
            padding: '10px 12px',
            backgroundColor: '#f5f5f5',
            borderRadius: '6px',
            display: 'flex',
            gap: '16px',
            alignItems: 'center'
          }}>
          {/* Application ID Filter */}
          <div className="md-filter-section" style={{ flex: 1, position: 'relative' }}>
            <label htmlFor="applicationIdFilter" className="md-filter-label" style={{
              display: 'block',
              marginBottom: '5px',
              fontWeight: '500',
              fontSize: '14px'
            }}>Filter by Application ID</label>
            <input
              type="text"
              id="applicationIdFilter"
              value={selectedApplicationId === 'All' ? '' : selectedApplicationId}
              onChange={(e) => setSelectedApplicationId(e.target.value || 'All')}
              placeholder="Enter Application ID..."
              className="md-input"
              style={{
                width: '100%',
                padding: '8px 30px 8px 8px',
                borderRadius: '4px',
                border: '1px solid #ddd',
                fontSize: '14px'
              }}
            />
            {selectedApplicationId !== 'All' && (
              <button
                onClick={() => setSelectedApplicationId('All')}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '28px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '16px',
                  color: '#666',
                  width: '20px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Clear Application ID filter"
              >
                ×
              </button>
            )}
          </div>
          
          {/* Application Owner Filter - Only show for Administrator and APR Analyst roles */}
          {(currentUser?.role === 'Administrator' || currentUser?.role === 'APR Analyst') && (
            <div className="md-filter-section" style={{ flex: 1 }}>
              <label htmlFor="ownerFilter" className="md-filter-label" style={{
                display: 'block',
                marginBottom: '5px',
                fontWeight: '500',
                fontSize: '14px'
              }}>Filter by Application Owner</label>
              <select
                id="ownerFilter"
                value={selectedOwner}
                onChange={(e) => setSelectedOwner(e.target.value)}
                className="md-select"
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd'
                }}
              >
                <option value="All">All Owners</option>
                {Array.from(new Set(visibleApplications
                  .map(app => app.applicationOwner)
                  .filter(owner => owner && owner.trim() !== '')
                )).sort().map(owner => (
                  <option key={owner} value={owner}>{owner}</option>
                ))}
              </select>
            </div>
          )}

          {/* View Mode Toggle */}
          <div className="md-filter-section" style={{ minWidth: '180px' }}>
            <label className="md-filter-label" style={{
              display: 'block',
              marginBottom: '5px',
              fontWeight: '500',
              fontSize: '14px'
            }}>View Mode</label>
            <div style={{
              display: 'flex',
              gap: '4px',
              backgroundColor: '#e5e5e5',
              borderRadius: '6px',
              padding: '4px'
            }}>
              <button
                onClick={() => setViewMode('tree')}
                style={{
                  flex: 1,
                  padding: '6px 12px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  backgroundColor: viewMode === 'tree' ? '#fff' : 'transparent',
                  color: viewMode === 'tree' ? '#1a73e8' : '#666',
                  boxShadow: viewMode === 'tree' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s'
                }}
              >
                🌳 Tree
              </button>
              <button
                onClick={() => setViewMode('flat')}
                style={{
                  flex: 1,
                  padding: '6px 12px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  backgroundColor: viewMode === 'flat' ? '#fff' : 'transparent',
                  color: viewMode === 'flat' ? '#1a73e8' : '#666',
                  boxShadow: viewMode === 'flat' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s'
                }}
              >
                📋 Flat
              </button>
            </div>
          </div>
        </div>
        )}
      </div>
      {/* End Fixed Header Section */}

      {/* Scrollable Data Section */}
      <div className="scrollable-data-section">
        {/* Loading State */}
        {loading && (
          <div className="empty-state">
            <div className="empty-state-icon">⏳</div>
            <h3>Loading Applications for Q&A</h3>
            <p>Please wait while we fetch applications from In-Process APR models...</p>
          </div>
        )}

        {/* Applications Display - Tree or Flat View */}
        {!loading && modelGroups.length === 0 && filteredApplications.length === 0 ? (
          <div className="md-empty-state">
            <div className="md-empty-state__icon">❓</div>
            <h3 className="md-empty-state__title">No Applications Available for Q&A</h3>
            <p className="md-empty-state__description">
              {inProcessApplications.length === 0
                ? 'No applications are currently in "In-Process" status. Start Q&A on an APR model to see applications here.'
                : selectedApplicationId !== 'All'
                  ? 'No in-process applications match the selected Application ID filter.'
                  : 'No in-process applications available for Q&A.'
              }
            </p>
          </div>
        ) : !loading && viewMode === 'tree' ? (
          /* TREE VIEW */
          <div className="tree-view-container" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {modelGroups.map(group => {
              const filteredApps = filterGroupApplications(group.applications);
              if (filteredApps.length === 0) return null;
              const isExpanded = expandedModels.has(group.modelId);
              const stats = getModelStats({ ...group, applications: filteredApps });

              return (
                <div key={group.modelId} className="model-tree-node" style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}>
                  {/* Model Header (Parent Node) */}
                  <div
                    onClick={() => toggleModelExpansion(group.modelId)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '14px 16px',
                      backgroundColor: '#f8f9fa',
                      cursor: 'pointer',
                      borderBottom: isExpanded ? '1px solid #e0e0e0' : 'none',
                      transition: 'background-color 0.2s'
                    }}
                  >
                    {/* Expand/Collapse Icon */}
                    <span style={{
                      marginRight: '12px',
                      fontSize: '14px',
                      color: '#666',
                      transition: 'transform 0.2s',
                      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'
                    }}>
                      ▶
                    </span>

                    {/* Model Icon & Name */}
                    <span style={{ marginRight: '10px', fontSize: '1.3rem' }}>
                      {group.modelType === 'Model' ? '📁' : '🏃'}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', fontSize: '15px', color: '#333' }}>
                        {group.modelName}
                      </div>
                      {group.frameworkName && (
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                          Framework: {group.frameworkName}
                        </div>
                      )}
                    </div>

                    {/* Stats Badges */}
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <span style={{
                        padding: '4px 10px',
                        backgroundColor: '#e3f2fd',
                        color: '#1565c0',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {stats.totalApps} apps
                      </span>
                      {stats.completedApps > 0 && (
                        <span style={{
                          padding: '4px 10px',
                          backgroundColor: '#e8f5e9',
                          color: '#2e7d32',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          {stats.completedApps} complete
                        </span>
                      )}
                      {stats.inProgressApps > 0 && (
                        <span style={{
                          padding: '4px 10px',
                          backgroundColor: '#fff3e0',
                          color: '#ef6c00',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          {stats.inProgressApps} in progress
                        </span>
                      )}
                      <div style={{
                        width: '80px',
                        height: '6px',
                        backgroundColor: '#e0e0e0',
                        borderRadius: '3px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${stats.completionPercentage}%`,
                          height: '100%',
                          backgroundColor: stats.completionPercentage === 100 ? '#4caf50' : '#1a73e8',
                          transition: 'width 0.3s'
                        }} />
                      </div>
                      <span style={{ fontSize: '12px', color: '#666', minWidth: '35px' }}>
                        {stats.completionPercentage}%
                      </span>
                    </div>
                  </div>

                  {/* Applications Table (Children) */}
                  {isExpanded && (
                    <div className="md-table-container" style={{ borderRadius: 0 }}>
                      <table className="md-table" style={{ marginBottom: 0 }}>
                        <thead className="md-table__header">
                          <tr>
                            <th className="md-table__cell md-table__cell--header" style={{ width: '28%' }}>Application</th>
                            <th className="md-table__cell md-table__cell--header" style={{ width: '18%' }}>Owner</th>
                            <th className="md-table__cell md-table__cell--header" style={{ width: '100px' }}>Q&A Status</th>
                            <th className="md-table__cell md-table__cell--header" style={{ width: '100px', textAlign: 'center' }}>Progress</th>
                            <th className="md-table__cell md-table__cell--header md-table__cell--actions" style={{ width: '150px', textAlign: 'center' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody className="md-table__body">
                          {filteredApps.map((app, index) => {
                            const qaStatus = getQAStatus(app);
                            const appQAStatus = qaStatuses[app.id];
                            const answeredQuestions = appQAStatus?.answeredQuestions || 0;
                            const totalQuestions = appQAStatus?.totalQuestions || 0;
                            const isComplete = qaStatus.status === 'Complete';
                            const isInProcess = qaStatus.status === 'In-Process';
                            const isNotStarted = qaStatus.status === 'Not Started';
                            const hasDisposition = !!finalDispositions[app.id];
                            const isLocked = hasDisposition;

                            return (
                              <tr
                                key={app.id}
                                className={`md-table__row ${isLocked ? 'md-table__row--transferred' : ''}`}
                                style={isLocked ? {
                                  opacity: 0.6,
                                  backgroundColor: '#f5f5f5'
                                } : undefined}
                                title={isLocked ? `Final Disposition set: ${finalDispositions[app.id]} - Assessment changes disabled` : undefined}
                              >
                                <td className="md-table__cell" style={{ width: '28%' }}>
                                  <div className="md-app-info">
                                    <div className="md-app-info__primary" style={{ fontSize: '14px' }}>{app.name}</div>
                                    {app.applicationIdCode && (
                                      <div className="md-app-info__secondary" style={{ fontSize: '13px', color: '#666' }}>ID: {app.applicationIdCode}</div>
                                    )}
                                  </div>
                                </td>
                                <td className="md-table__cell" style={{ width: '18%' }}>
                                  <div className="md-owner-info">
                                    <div className="md-owner-info__name" style={{ fontSize: '14px' }}>{app.applicationOwner || 'Not assigned'}</div>
                                  </div>
                                </td>
                                <td className="md-table__cell" style={{ width: '100px' }}>
                                  <div className="md-status-info">
                                    <div className="md-status-info__primary" style={{ fontSize: '14px' }}>{qaStatus.status}</div>
                                  </div>
                                </td>
                                <td className="md-table__cell" style={{ width: '100px', textAlign: 'center' }}>
                                  <div className="md-progress-info" style={{ justifyContent: 'center' }}>
                                    <span className="md-progress-text" style={{ fontSize: '14px' }}>{answeredQuestions}/{totalQuestions}</span>
                                  </div>
                                </td>
                                <td className="md-table__cell md-table__cell--actions" style={{ width: '150px', textAlign: 'center' }}>
                                  <div className="qa-action-buttons" style={{ display: 'flex', justifyContent: 'center', gap: '6px', alignItems: 'center' }}>
                                    {!isLocked && isNotStarted && (
                                      <button
                                        className="qa-action-btn qa-action-btn--start qa-action-btn--compact"
                                        onClick={(e) => handleStartQA(app, e)}
                                        title="Start Q&A assessment"
                                        style={{ fontSize: '14px' }}
                                      >
                                        <span className="qa-action-btn__text" style={{ fontSize: '14px' }}>Start Q&A</span>
                                      </button>
                                    )}
                                    {!isLocked && isInProcess && (
                                      <>
                                        <button
                                          className="qa-action-btn qa-action-btn--continue qa-action-btn--compact"
                                          onClick={(e) => handleStartQA(app, e)}
                                          title="Continue Q&A assessment"
                                          style={{ fontSize: '14px' }}
                                        >
                                          <span className="qa-action-btn__text" style={{ fontSize: '14px' }}>Continue</span>
                                        </button>
                                        <button
                                          className="qa-action-btn qa-action-btn--view qa-action-btn--compact"
                                          onClick={(e) => handleViewQA(app, e)}
                                          title="View progress"
                                          style={{ fontSize: '14px' }}
                                        >
                                          <span className="qa-action-btn__text" style={{ fontSize: '14px' }}>View</span>
                                        </button>
                                      </>
                                    )}
                                    {(isComplete || isLocked) && (
                                      <button
                                        className="qa-action-btn qa-action-btn--view qa-action-btn--compact"
                                        onClick={(e) => handleViewQA(app, e)}
                                        title={isLocked ? "View assessment (Locked)" : "View assessment"}
                                        style={{ fontSize: '14px' }}
                                      >
                                        <span className="qa-action-btn__text" style={{ fontSize: '14px' }}>View</span>
                                      </button>
                                    )}
                                    {isLocked && (
                                      <span style={{ fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
                                        🔒 Final
                                      </span>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : !loading && (
          /* FLAT VIEW (Original Table) */
          <div className="md-card md-card--elevated">
            <div className="md-table-container">
              <table className="md-table">
                <thead className="md-table__header">
                  <tr>
                    <th className="md-table__cell md-table__cell--header" style={{ width: '28%' }}>Application</th>
                    <th className="md-table__cell md-table__cell--header" style={{ width: '18%' }}>Owner</th>
                    <th className="md-table__cell md-table__cell--header" style={{ width: '100px' }}>Q&A Status</th>
                    <th className="md-table__cell md-table__cell--header" style={{ width: '100px', textAlign: 'center' }}>Progress</th>
                    <th className="md-table__cell md-table__cell--header md-table__cell--actions" style={{ width: '150px', textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody className="md-table__body">
                  {filteredApplications.map((app, index) => {
                    const hasDisposition = !!finalDispositions[app.id];
                    const isLocked = hasDisposition;
                    return (
                    <tr
                      key={app.id}
                      className={`md-table__row ${isLocked ? 'md-table__row--transferred' : ''}`}
                      style={isLocked ? {
                        opacity: 0.6,
                        backgroundColor: '#f5f5f5'
                      } : undefined}
                      title={isLocked ? `Final Disposition set: ${finalDispositions[app.id]} - Assessment changes disabled` : undefined}
                    >
                      <td className="md-table__cell" style={{ width: '28%' }}>
                        <div className="md-app-info">
                          <div className="md-app-info__primary" style={{ fontSize: '14px' }}>{app.name}</div>
                          {app.applicationIdCode && (
                            <div className="md-app-info__secondary" style={{ fontSize: '13px', color: '#666' }}>ID: {app.applicationIdCode}</div>
                          )}
                        </div>
                      </td>

                      <td className="md-table__cell" style={{ width: '18%' }}>
                        <div className="md-owner-info">
                          <div className="md-owner-info__name" style={{ fontSize: '14px' }}>{app.applicationOwner || 'Not assigned'}</div>
                        </div>
                      </td>

                      <td className="md-table__cell" style={{ width: '100px' }}>
                        {(() => {
                          const qaStatus = getQAStatus(app);

                          return (
                            <div className="md-status-info">
                              <div className="md-status-info__primary" style={{ fontSize: '14px' }}>
                                {qaStatus.status}
                              </div>
                            </div>
                          );
                        })()}
                      </td>

                      <td className="md-table__cell" style={{ width: '100px', textAlign: 'center' }}>
                        {(() => {
                          const appQAStatus = qaStatuses[app.id];
                          const answeredQuestions = appQAStatus?.answeredQuestions || 0;
                          const totalQuestions = appQAStatus?.totalQuestions || 0;

                          return (
                            <div className="md-progress-info" style={{ justifyContent: 'center' }}>
                              <span className="md-progress-text" style={{ fontSize: '14px' }}>{answeredQuestions}/{totalQuestions}</span>
                            </div>
                          );
                        })()}
                      </td>

                      <td className="md-table__cell md-table__cell--actions" style={{ width: '150px', textAlign: 'center' }}>
                        {(() => {
                          const qaStatus = getQAStatus(app);
                          const isComplete = qaStatus.status === 'Complete';
                          const isInProcess = qaStatus.status === 'In-Process';
                          const isNotStarted = qaStatus.status === 'Not Started';

                          console.log('ApplicationPortfolioQA: Rendering action buttons for', app.name, 'Q&A Status:', qaStatus, 'isLocked:', isLocked);

                          // If locked due to final disposition, only show View button
                          if (isLocked) {
                            return (
                              <div className="qa-action-buttons" style={{ display: 'flex', justifyContent: 'center', gap: '6px' }}>
                                <button
                                  className="qa-action-btn qa-action-btn--view qa-action-btn--compact"
                                  onClick={(e) => handleViewQA(app, e)}
                                  title="View assessment (Locked)"
                                >
                                  <span className="qa-action-btn__text">View</span>
                                </button>
                                <span style={{ fontSize: '11px', color: '#666', fontStyle: 'italic' }}>
                                  🔒 Final
                                </span>
                              </div>
                            );
                          }

                          return (
                            <div className="qa-action-buttons" style={{ display: 'flex', justifyContent: 'center', gap: '6px' }}>
                              {/* For Application Owners - show appropriate buttons based on status */}
                              {currentUser?.role === 'Application Owner' && (
                                <>
                                  {isNotStarted && (
                                    <button
                                      className="qa-action-btn qa-action-btn--start qa-action-btn--compact"
                                      onClick={(e) => {
                                        console.log('ApplicationPortfolioQA: Start button clicked for', app.name);
                                        handleStartQA(app, e);
                                      }}
                                      title="Start Q&A assessment for this application"
                                    >
                                      <span className="qa-action-btn__icon">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <polygon points="5,3 19,12 5,21"/>
                                        </svg>
                                      </span>
                                      <span className="qa-action-btn__text">Start Q&A</span>
                                    </button>
                                  )}

                                  {isInProcess && (
                                    <>
                                      <button
                                        className="qa-action-btn qa-action-btn--continue qa-action-btn--compact"
                                        onClick={(e) => {
                                          console.log('ApplicationPortfolioQA: Continue button clicked for', app.name);
                                          handleStartQA(app, e);
                                        }}
                                        title="Continue Q&A assessment for this application"
                                      >
                                        <span className="qa-action-btn__icon">
                                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polygon points="5,3 19,12 5,21"/>
                                          </svg>
                                        </span>
                                        <span className="qa-action-btn__text">Continue Q&A</span>
                                      </button>
                                      <button
                                        className="qa-action-btn qa-action-btn--view qa-action-btn--compact"
                                        onClick={(e) => {
                                          console.log('ApplicationPortfolioQA: View button clicked for', app.name);
                                          handleViewQA(app, e);
                                        }}
                                        title="View current Q&A assessment progress"
                                      >
                                        <span className="qa-action-btn__icon">
                                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                            <circle cx="12" cy="12" r="3"/>
                                          </svg>
                                        </span>
                                        <span className="qa-action-btn__text">View Progress</span>
                                      </button>
                                    </>
                                  )}

                                  {isComplete && (
                                    <button
                                      className="qa-action-btn qa-action-btn--view qa-action-btn--compact"
                                      onClick={(e) => {
                                        console.log('ApplicationPortfolioQA: View button clicked for', app.name);
                                        handleViewQA(app, e);
                                      }}
                                      title="View completed Q&A assessment"
                                    >
                                      <span className="qa-action-btn__icon">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                          <circle cx="12" cy="12" r="3"/>
                                        </svg>
                                      </span>
                                      <span className="qa-action-btn__text">View Assessment</span>
                                    </button>
                                  )}
                                </>
                              )}

                              {/* For Administrators and APR Analysts - show same functionality as Application Owners */}
                              {(currentUser?.role === 'Administrator' || currentUser?.role === 'APR Analyst') && (
                                <>
                                  {isNotStarted && (
                                    <button
                                      className="qa-action-btn qa-action-btn--start qa-action-btn--compact"
                                      onClick={(e) => {
                                        console.log('ApplicationPortfolioQA: Start button clicked for', app.name);
                                        handleStartQA(app, e);
                                      }}
                                      title="Start Q&A assessment for this application"
                                    >
                                      <span className="qa-action-btn__icon">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <polygon points="5,3 19,12 5,21"/>
                                        </svg>
                                      </span>
                                      <span className="qa-action-btn__text">Start Q&A</span>
                                    </button>
                                  )}
                                  
                                  {isInProcess && (
                                    <>
                                      <button 
                                        className="qa-action-btn qa-action-btn--continue qa-action-btn--compact"
                                        onClick={(e) => {
                                          console.log('ApplicationPortfolioQA: Continue button clicked for', app.name);
                                          handleStartQA(app, e);
                                        }}
                                        title="Continue Q&A assessment for this application"
                                      >
                                        <span className="qa-action-btn__icon">
                                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polygon points="5,3 19,12 5,21"/>
                                          </svg>
                                        </span>
                                        <span className="qa-action-btn__text">Continue Q&A</span>
                                      </button>
                                      <button 
                                        className="qa-action-btn qa-action-btn--view qa-action-btn--compact"
                                        onClick={(e) => {
                                          console.log('ApplicationPortfolioQA: View button clicked for', app.name);
                                          handleViewQA(app, e);
                                        }}
                                        title="View current Q&A assessment progress"
                                      >
                                        <span className="qa-action-btn__icon">
                                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                            <circle cx="12" cy="12" r="3"/>
                                          </svg>
                                        </span>
                                        <span className="qa-action-btn__text">View Progress</span>
                                      </button>
                                    </>
                                  )}
                                  
                                  {isComplete && (
                                    <button 
                                      className="qa-action-btn qa-action-btn--view qa-action-btn--compact"
                                      onClick={(e) => {
                                        console.log('ApplicationPortfolioQA: View button clicked for', app.name);
                                        handleViewQA(app, e);
                                      }}
                                      title="View completed Q&A assessment"
                                    >
                                      <span className="qa-action-btn__icon">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                          <circle cx="12" cy="12" r="3"/>
                                        </svg>
                                      </span>
                                      <span className="qa-action-btn__text">View Assessment</span>
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      {/* End Scrollable Data Section */}

      {/* Q&A View Modal */}
      {showViewModal && selectedApplication && (
        <>
          {console.log('ApplicationPortfolioQA: Rendering QAViewModal with props:', {
            applicationId: selectedApplication.id,
            applicationName: selectedApplication.name,
            aprRunId: selectedApplication.aprRunId,
            isOpen: showViewModal
          })}
          <QAViewModal
            applicationId={selectedApplication.id}
            applicationName={selectedApplication.name}
            aprRunId={selectedApplication.aprRunId}
            isOpen={showViewModal}
            onClose={handleCloseViewModal}
          />
        </>
      )}

      {/* Q&A Assessment Modal */}
      {showQAModal && selectedApplication && (
        <QAAssessmentModal
          applicationId={selectedApplication.id}
          applicationName={selectedApplication.name}
          aprRunId={selectedApplication.aprRunId}
          isOpen={showQAModal}
          onClose={handleCloseQAModal}
          onComplete={handleCompleteAssessment}
        />
      )}
    </div>
  );
};

export default ApplicationPortfolioQA;